import React, { FunctionComponent, useState, useEffect,useContext } from 'react';
import { useDispatch } from 'react-redux';
import actions from '@state/actions';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { getBrandIdByOEMName } from 'utils/editor';
import { useSelector } from 'react-redux';
import selectors from '@state/selectors';
import { useLocation } from 'react-router';
import { PermissionEnum } from '@state/designs/types';

export const DesignsContext = React.createContext(undefined as any);

export interface DesignsProviderProps {
}

export const DesignsProvider: FunctionComponent<any> = (props: React.PropsWithChildren<DesignsProviderProps>) => {
    const dsConfig = useSelector(selectors.dsConfig.getDSConfig);
    const [searching, setSearching]= useState(false);
    const [searchValue, setSearchValue]= useState('');
    const [searchSelectedOne, setSearchSelectedOne]= useState(null);
    const [searchSelectedAll, setSearchSelectedAll]= useState(false);

    const [allFilters, setAllf]=useState([] as any);
    const [filters, setFilters]= useState({oem:[], platform:[],format:[], offerType:[]} as any);

    const [searchFolder, setSearchFolder]= useState('');
    
    const [sharedFiles, setSharedFiles]= useState<any>({open: false, permission: PermissionEnum.EDIT});

    const [sortByMenu, setSortBy]=useState([
        { title: "Name: ", extra:'(A-Z)', text:"A-Z", query:"c.formatName ASC"},
        { title: "Name: ", extra:'(Z-A)', text:"Z-A", query:"c.formatName DESC"},
        { title: "# of Sizes: ", extra:'(Small to Large)', text:"Smallest to Largest", query:"c.tags.formatCounter ASC"},
        { title: "# of Sizes: ", extra:'(Large to Small)', text:"Largest to Smallest", query:"c.tags.formatCounter DESC"},
        { title: "Last Edited: ", extra:'(New to Old)', text:"Newest to Oldest", query:"c.updateDate DESC"},
        { title: "Last Edited: ", extra:'(Old to New)', text:"Oldest to Newest", query:"c.updateDate ASC"}
    ] as any);
    const [sortByQuery, setSortByQuery]=useState('');
    const [sortByText, setSortByText]=useState('');

    const dispatch = useDispatch();
    const authContext = useContext(AuthContext);

    const location = useLocation();
    useEffect(() => {
        if (location.pathname!=="/shared-files" && sharedFiles.open) setSharedFiles({open: false, permission: PermissionEnum.EDIT});
        setSearching(false);
        if (searchSelectedOne) setSearchSelectedOne(null);
        if (searchSelectedAll) setSearchSelectedAll(false);
        setSortByQuery('');
        setSortByText('');
        setAllf([] as any);
        setFilters({oem:[], platform:[],format:[], offerType:[]} as any);
    }, [location])

    useEffect(() => {
        if (!searching) {
            if (searchSelectedOne) setSearchSelectedOne(null);
            if (searchSelectedAll) setSearchSelectedAll(false);
        }
        if (searching) setFilters({oem:[], platform:[],format:[], offerType:[]} as any);
    }, [searching])

    useEffect(() => {
        if (searching) setSearching(false);
        if (searchSelectedOne) setSearchSelectedOne(null);
        if (searchSelectedAll) setSearchSelectedAll(false);
    }, [searchFolder])
    
    useEffect(() => {
        if (sortByQuery==='') setSortByText('');
    }, [sortByQuery])

    const cleanFilters = () => {
        if (allFilters.length>0) {
            setAllf([]);
            setFilters({oem:[], platform:[],format:[], offerType:[]} as any);
        }
    }

    const fetchCounter = (folder:string) => {
        let query = getQuerySearchFilters(folder);
        dispatch(actions.templates.setCounter({} as any));
        return new Promise((resolve, reject) => {
            dispatch(actions.designs.fetchCounterDesigns('?filter='+query,(result, error) => {
                if (error) reject(error)
                else resolve(result);
            }))
        });
    }

    const getQuerySearchFilters = (folder:string) => {
        const state= `='ACTIVE'`;
        let query='';

        const userData = {
            email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
            name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User"
          };

        let searchFolderQuery = getQuerySearchByFolder(folder);
        let filterQuery = getFilterQuery();

        let user: string = sharedFiles.open ? ``:`c.createdBy.email='${userData.email}' and `;

        if(allFilters.length>0 && searching){
            query=`${user} c.designStatus${state} ${filterQuery} and ${searchFolderQuery}(c.formatName LIKE "%${searchValue.toLowerCase()}%" or udf.searchCustomTagsDesign(c.tags.customTags, "${searchValue.toLowerCase()}"))`;
        }
        else if (allFilters.length>0 ){
            query=`(${searchFolderQuery} ${user} c.designStatus${state} ${filterQuery})`;
        }
        else if (searching){
            query=`${user} c.designStatus${state} and ${searchFolderQuery}(c.formatName LIKE "%${searchValue.toLowerCase()}%" or udf.searchCustomTagsDesign(c.tags.customTags, "${searchValue.toLowerCase()}"))`;
        }
        query = encodeURIComponent(query);
        return query;
    }

    const getQuerySearchByFolder = (folder:string) => {
        let searchByFolder = '';
        if (searchFolder!=='') folder = searchFolder;

        if ( sharedFiles.open){
            searchByFolder = ` ARRAY_CONTAINS(c.usersPermission, {"email": "${authContext?.userData.profile.email??''}", "permission": ${folder}}) and`;
        } else {
            if (folder!=='' && folder!=='all') searchByFolder = ` c.folder='${folder}' and`;
            if (folder==='favorite') searchByFolder = ` c.favorite=true and`;
        }
        return searchByFolder;
    }

    const getQuerySearch = () => {
        let searchQuery = '';

        if (searching){
            searchQuery=` and (c.formatName LIKE "%${searchValue.toLowerCase()}%" or udf.searchCustomTagsDesign(c.tags.customTags, "${searchValue.toLowerCase()}"))`;
        }
        return searchQuery;
    }

    const handleChangeFilter=(value:any, type:string, add:boolean)=>{
        let typeFilters=filters[type]
        if(add) {
            typeFilters.push(value);
            setAllf([...allFilters, value]);
        }
        else {
            typeFilters=typeFilters.filter((item:any) => item !== value);
            setAllf(allFilters.filter((item:any) => item !== value));
        }
        
        setFilters({...filters, [type]: typeFilters});
    }

    const getFilterQuery = () => {
        let filtersQuery = '';
        if(filters){
            Object.keys(filters).forEach((key) => {
                let columQuery = '';
                if (filters[key].length>0){
                  columQuery = ` and c.tags.${key} IN (`;
      
                  filters[key].forEach((text:string, idx:number) => {
                    if(key==='oem') columQuery=columQuery+`'${getBrandIdByOEMName(text, dsConfig)}'`;
                    else columQuery=columQuery+`'${text}'`;
                    if (idx < filters[key].length-1)columQuery=columQuery+`,`;
                  });
      
                  columQuery=columQuery+`) `;
                }
                filtersQuery=filtersQuery+columQuery;
            });
        }
        return filtersQuery;
    }

    const handleRemoveFilterChip = (index: number) => {
        let value= allFilters[index];
        setAllf(allFilters.filter((val:any,idx:number) => index !== idx));

        var type = Object.keys(filters).find(k=>filters[k].includes(value));
        if(type){
            let newList=filters[type].filter((item:any) => item !== value)
            setFilters({...filters, [type]: newList});
        }
    }

    const handleChangeSortBy = (index: number) => {
        setSortByText(sortByMenu[index].text);
        setSortByQuery(sortByMenu[index].query);
    }
    return (
        <DesignsContext.Provider value={{
            searching,
            setSearching: (val:boolean)=>setSearching(val),
            searchValue,
            setSearchValue: (val:string)=>setSearchValue(val),
            searchFolder,
            setSearchFolder: (val:string)=>setSearchFolder(val),
            searchSelectedOne,
            setSearchSelectedOne: (val:any)=>setSearchSelectedOne(val),
            searchSelectedAll,
            setSearchSelectedAll: (val:boolean)=>setSearchSelectedAll(val),
            getQuerySearchByFolder: (val:string)=>getQuerySearchByFolder(val),
            getQuerySearch: ()=>getQuerySearch(),
            filters,
            setFilters: (val:any)=>setFilters(val),
            allFilters,
            setAllf: (val:any)=>setAllf(val),
            handleChangeFilter: (val:any, type:string, add:boolean)=>handleChangeFilter(val, type, add),
            getFilterQuery: ()=>getFilterQuery(),
            handleRemoveFilterChip: (val:number)=>handleRemoveFilterChip(val),
            fetchCounter: (val:string)=>fetchCounter(val),
            cleanFilters: ()=>cleanFilters(),
            sortByMenu,
            sortByText,
            sortByQuery,
            setSortByQuery: (val:string)=>setSortByQuery(val),
            handleChangeSortBy: (val:number)=>handleChangeSortBy(val),
            sharedFiles,
            setSharedFiles: (val:any)=>setSharedFiles(val)
        }}>
            {props.children}
        </DesignsContext.Provider>
    )
}